import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/utils';

interface FilterDropdownProps {
    label: string;
    isActive?: boolean;
    badge?: number;
    children?: React.ReactNode;
    className?: string;
    align?: 'left' | 'right';
}

export function FilterDropdown({ label, isActive, badge, children, className, align = 'left' }: FilterDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <Button
                variant="outline"
                size="md"
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "h-10 border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-300 gap-2 font-medium rounded-md",
                    isActive && "border-brand-gold/50 text-brand-gold bg-brand-gold/5",
                    isOpen && "border-zinc-700 bg-zinc-800 text-brand-mist",
                    className
                )}
            >
                <span>{label}</span>
                {badge !== undefined && badge > 0 && (
                    <span className="ml-0.5 inline-flex h-4 min-w-[1rem] px-1 items-center justify-center rounded-full bg-brand-gold text-[10px] font-bold text-black leading-none">
                     {badge}
                   </span>
                )}
                <div className="w-px h-3 bg-zinc-800 mx-1 opacity-50" />
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform text-zinc-500", isOpen && "rotate-180", isActive && "text-brand-gold")} />
            </Button>

            {isOpen && (
                <div className={cn(
                    "absolute top-full mt-2 z-30 w-72 rounded-md border border-zinc-800 bg-zinc-950 shadow-2xl p-0 animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5 max-h-[85vh] overflow-y-auto overflow-x-hidden custom-scrollbar",
                    align === 'left' ? "left-0" : "right-0"
                )}
                >
                    {children}
                </div>
            )}
        </div>
    );
}
